import { generatePath, useMatch, useNavigate } from 'react-router-dom';

import { createForm, useCreateFormStore } from '@components/form';
import { RelativeSpinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useMoveJourney } from 'api/journeys/hooks';
import { JourneyType } from 'api/types';
import { useWorkspaces } from 'api/workspaces/hooks';
import { routePaths } from 'ui/authenticated/navigation';

export const useMoveJourneyModal = () => useModal(MoveJourneyModal);

type MoveJourneyData = {
  workspace: string;
};

const Form = createForm<MoveJourneyData>();

export const MoveJourneyModal = ({ journey }: { journey: JourneyType }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const navigate = useNavigate();
  const match = useMatch(routePaths.journeyDetails);
  const isJourneyDetails = Boolean(match);

  const moveMutation = useMoveJourney(journey.shortId);
  const { data: workspaces } = useWorkspaces();

  const handleSubmit = async (data: MoveJourneyData) => {
    await moveMutation.mutateAsync(data.workspace);
    if (isJourneyDetails) {
      const newPath = generatePath(routePaths.journeyDetails, {
        journeyId: journey.shortId,
        workspaceId: data.workspace,
        '*': match?.params['*'] ?? null,
      });
      navigate(newPath, { replace: false });
    }
    hideModal();
  };

  if (!workspaces) {
    return <RelativeSpinner />;
  }

  return (
    <Modal>
      <Form store={formStore} onSubmit={handleSubmit}>
        <Modal.SimpleHeader title={t('title')} />
        <Modal.Content>
          <Stack space={6}>
            <Text text={t('description')} />
            <Separator />
            <Form.Select
              id="workspace"
              label={t('workspace')}
              options={workspaces
                .filter((workspace) => workspace.shortId !== journey.workspace)
                .map((workspace) => ({
                  value: workspace.shortId,
                  label: workspace.title,
                }))}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('confirm')}
              variant="primary-blue"
              disabled={moveMutation.isLoading}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Move journey',
    workspace: 'Workspace',
    confirm: 'Move journey',
    cancel: 'Cancel',
    description:
      'Moving a journey to a workspace also moves associated forms and documents. If the forms or documents are used by other journeys in this workspace, they will be copied instead of moved',
    notPublishedDisabled: 'Cannot include travellers unless a journey is published',
  },
};
